import './RichTextPopup.css';
import React from 'react';
import { EditorState } from 'draft-js';
import { IFormInputProps } from '../../interfaces/IFormInputProps';
import { IRichTextInputToolbar, RichTextInputToolbar } from './RichTextInputToolbar';
import { IFormInputComponent } from '../../interfaces/IFormInputComponent';
import { toRichtext, isEquialsStates, RichTextValue, Richtext, getMultilineHtml } from './common';
import { css, IconButton, MessageBar, MessageBarType, Modal } from 'office-ui-fabric-react';
import RichEditor from './RichEditor';
import { EditorUtils } from './EditorUtils';
import * as Metadata from '../../../../entities/Metadata';
import { useDebounce } from '../../../utils/effects';

type RichTextPopupProps = IFormInputProps<RichTextValue, HTMLTextAreaElement> & {
    placeholder?: string;
    field?: Metadata.Field;
    onPopupDismiss?: () => void;
}

const onChangeDebounceDelay: number = 100;

export const RichTextPopup = React.forwardRef<IFormInputComponent, RichTextPopupProps>((props, ref) => {
    const richEditor = React.useRef<HTMLElement | null>(null);
    const toolbar = React.useRef<IRichTextInputToolbar | null>(null);

    const [editorState, setEditorState] = React.useState<EditorState>(EditorUtils.buildEditorState(props.value));
    const [richValue, setRichValue] = React.useState(props.value);
    const [showDismissMessage, setShowDismissMessage] = React.useState<boolean>(false);
    const [isDirty, setIsDirty] = React.useState<boolean>(false);
    const readonly = !!props.disabled || !!props.readOnly;

    const getErrorMessage = (value: RichTextValue): string | undefined => props.validator?.getErrorMessage(value);
    const [errorMessage, setErrorMessage] = React.useState(getErrorMessage(props.value));

    const updateErrorMessage = React.useCallback((value: RichTextValue) => {
        const newErrorMessage = getErrorMessage(value);
        if (errorMessage !== newErrorMessage) {
            setErrorMessage(newErrorMessage)
        }
    }, [props.validator, errorMessage]);

    React.useEffect(() => {
        if (getMultilineHtml(richValue) !== getMultilineHtml(props.value)) {
            setEditorState(EditorUtils.buildEditorState(props.value));
            updateErrorMessage(props.value);
        }
    }, [props.value]);

    React.useEffect(() => updateErrorMessage(props.value), [props.validator]);

    const focus = React.useCallback(() => richEditor.current?.focus(), [richEditor, readonly]);
    
    React.useEffect(() => focus(), []);

    React.useImperativeHandle(ref, (): IFormInputComponent => ({ focus: () => focus() }));

    const onEditComplete = React.useCallback((canceled?: boolean) => {
        setIsDirty(false);
        const newValue = canceled ? undefined : toRichtext(editorState);
        props.onEditComplete?.(newValue);
    }, [props.value, editorState, isDirty, readonly]);

    const onChanged = useDebounce((value: Richtext) => {
        setRichValue(value);
        updateErrorMessage(value);
        props.onChanged?.(value);
    }, onChangeDebounceDelay);

    const onEditorStateChange = React.useCallback((state?: EditorState) => {
        if (readonly || !state) {
            return;
        }

        if (isDirty || !isEquialsStates(editorState, state)) {
            setEditorState(state);
            if (!isDirty) {
                setIsDirty(true);
            }
            setShowDismissMessage(false);
            onChanged?.(toRichtext(state));
        } else {
            setEditorState(state);
        }
    }, [readonly, editorState, isDirty, props.onChanged]);

    const fieldLabel = props.field ? Metadata.getLabel(props.field) : undefined;

    return <Modal isOpen
        className='richtext-popup'
        onDismiss={(ev) => {
            ev?.preventDefault();
            if (isDirty) {
                setShowDismissMessage(true);
            } else {
                props.onPopupDismiss?.();
            }
        }}
    >
        <div className='richtext-wrapper'>
            <div className='header'>
                <div className={'label-container'}>
                    <span className='label' title={fieldLabel}>{fieldLabel ?? ""}</span>
                    {!!props.field && Metadata.isRequired(props.field) && <span className='required'> *</span>}
                </div>
                {!readonly && <RichTextInputToolbar
                    ref={toolbar}
                    editorState={editorState}
                    onCommand={(newEditorState) => {
                        onEditorStateChange(newEditorState);
                        focus();
                    }} />}
                <div className='save-buttons'>
                    {!readonly && <IconButton
                        iconProps={{ iconName: 'Save' }}
                        ariaLabel="Save"
                        title='Save and Close'
                        disabled={!!errorMessage}
                        onClick={() => onEditComplete()}
                    />}
                    <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        ariaLabel="Cancel"
                        title='Cancel'
                        onClick={() => props.onPopupDismiss?.()}
                    />
                </div>
            </div>
            {showDismissMessage && <MessageBar messageBarType={MessageBarType.warning}>
                You have unsaved changes. Please click Save to save the changes or Cancel to discard them.
            </MessageBar>}
            <div className={css(props.className, 'richtext', { invalid: !!errorMessage })}
                aria-hidden={true}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                    e.stopPropagation();
                    !readonly && focus();
                }}
            >
                {!!errorMessage && <div className='error-message'>{errorMessage}</div>}
                <RichEditor
                    className={css({ invalid: !!errorMessage })}
                    ref={richEditor}
                    editorState={editorState}
                    readOnly={readonly}
                    placeholder={props.placeholder}
                    openLinkEditor={toolbar.current?.openLinkEditor}
                    onEditorStateChange={onEditorStateChange}
                    spellCheck
                    useKeyTab
                />
            </div>
        </div>
    </Modal>;
})
